import React, { useEffect, useState } from 'react';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import PDFDownloader from '../../../Components/PDFDownloader/PDFDownloader';
import { 
  Card, 
  Table, 
  Button, 
  DatePicker, 
  Select, 
  Typography, 
  Space, 
  Divider, 
  Row, 
  Col,
  Popconfirm,
  Modal,
  Input,
  message
} from 'antd';
import { DownOutlined, UpOutlined, FilePdfOutlined, DeleteOutlined } from '@ant-design/icons';
import { openNotificationSuccess, openNotificationError } from './../../../Components/Notification/Notification';

import dayjs from "dayjs";
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const BestelScreen = () => {
  const { get, post, del, put } = AxiosCaller();
  const [bestellen, setBestellen] = useState([]); // Only to-order items
  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [datum, setDatum] = useState(null);
  const [leverancier, setLeverancier] = useState(null);
  const [leveranciers, setLeveranciers] = useState([]);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedBestelling, setSelectedBestelling] = useState(null);
  const [bestandsnaam, setBestandsnaam] = useState('');
  const [opmerking, setOpmerking] = useState('');

  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      setMaxHeight(0.7 * document.documentElement.clientHeight);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    fetchBestelGeschiedenis();
    fetchLeveranciers();
  }, []);

  useEffect(() => {
    fetchBestelGeschiedenis();
  }, [datum, leverancier]);

  const fetchBestelGeschiedenis = async () => {
    try {
      let formattedDate = null;
      if (datum) {
        formattedDate = dayjs(datum).format('YYYY-MM-DD');
      }
      
      const response = await post('/bestellingsGeschiedenis/filtered', {
        datum: formattedDate, 
        leverancier: leverancier
      });
  
      if (Array.isArray(response.data)) {
        // Only show items that are NOT ordered yet (isBesteld === false)
        const toOrderItems = response.data.filter((bestelling) => bestelling.isBesteld === false);
        setBestellen(toOrderItems);
      } else {
        openNotificationError("Foute data opgehaald");
      }
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij ophalen van de bestellingsgeschiedenis', error);
    }
  };

  const fetchLeveranciers = async () => {
    try {
      const response = await get('/leverancier');
      
      if (Array.isArray(response.data)) {
        setLeveranciers(response.data);
      } else {
        openNotificationError("Foute data opgehaalt")
      }
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij ophalen van leveranciers', error)
    }
  };

  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  const bestel = async (bestelling) => {
    try {
      const response = await post(`/bestellingsGeschiedenis/bestel/${bestelling._id}`);
      
      // Immediately remove from the local state for instant feedback
      setBestellen(prev => prev.filter(item => item._id !== bestelling._id));
      
      // Open modal for filename input
      setSelectedBestelling(response.data.bestelling);
      setBestandsnaam(generateDefaultFilename(response.data.bestelling));
      setOpmerking(response.data.bestelling.opmerking || '');
      setPdfModalVisible(true);
      
      openNotificationSuccess('Bestelling succesvol geplaatst.');
      
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij het plaatsen van de bestelling!', error);
      // Refresh from server in case of error to restore correct state
      await fetchBestelGeschiedenis();
    }
  };

  const generateDefaultFilename = (bestelling) => {
    const date = dayjs(bestelling.bestelDatum).format('DD-MM-YYYY');
    const leverancier = bestelling.leverancier.replace(/\s+/g, '_');
    return `bestelling_${leverancier}_${date}`;
  };

  const handlePdfGeneration = async () => {
    if (!bestandsnaam.trim()) {
      message.error('Voer een bestandsnaam in');
      return;
    }

    if (selectedBestelling) {
      try {
        // If there's an opmerking, update the bestelling in the database
        if (opmerking.trim() && opmerking !== selectedBestelling.opmerking) {
          const updatedBestelling = {
            opmerking: opmerking.trim()
          };
          
          await put(`/bestellingsGeschiedenis/${selectedBestelling._id}`, updatedBestelling);
        }

        // Generate PDF with the opmerking
        genereerPDF(
          { ...selectedBestelling, opmerking: opmerking.trim() }, 
          bestandsnaam
        );
        
        setPdfModalVisible(false);
        setBestandsnaam('');
        setOpmerking('');
        setSelectedBestelling(null);
        message.success('PDF wordt gegenereerd...');
      } catch (error) {
        console.error('Error updating bestelling:', error);
        openNotificationError('Fout bij bijwerken van opmerking', error);
      }
    }
  };

  const handlePdfModalCancel = () => {
    setPdfModalVisible(false);
    setBestandsnaam('');
    setOpmerking('');
    setSelectedBestelling(null);
  };

  const verwijderBestelling = async (bestelling) => {
    try {
      await del(`/bestellingsGeschiedenis/${bestelling._id}`);
      // Remove from local state immediately
      setBestellen(prev => prev.filter(item => item._id !== bestelling._id));
      openNotificationSuccess('Bestelling succesvol verwijderd!');
    } catch (error) {
      console.error(error);
      openNotificationError('Fout bij het verwijderen van de bestelling', error);
    }
  };

  const genereerPDF = (bestelling, bestandsnaam) => {
    const producten = [];
    
    if (bestelling.isGesplitst) {
      bestelling.producten.forEach((product) => {
        producten.push({
          klas: product.klas,
          leerkracht: product.leerkracht,
          product: product.product,
          datum: product.kookDatum,
          aantal: product.aantal,
          eenheid: product.eenheid,
          leverancier: bestelling.leverancier,
        });
      });

      const data = {
        datum: bestelling.bestelDatum,
        leverancier: bestelling.leverancier,
        producten: producten,
        opmerking: bestelling.opmerking || '',
      };

      PDFDownloader.generateVleesPDF(data, bestandsnaam);
    } else {
      bestelling.producten.forEach((product) => {
        producten.push({
          product: product.product,
          aantal: product.aantal,
          eenheid: product.eenheid,
          leverancier: bestelling.leverancier,
          datum: bestelling.Datum,
        });
      });
      
      const data = {
        datum: bestelling.bestelDatum,
        leverancier: bestelling.leverancier,
        producten: producten,
        opmerking: bestelling.opmerking || '',
      };
      
      PDFDownloader.generateProductPDF(data, bestandsnaam);
    }
  };

  return (
    <div className="Screen-container">
      <h1 className='title'>Bestellen</h1>
      
      {/* PDF Filename Modal */}
      <Modal
        title="Bestelling Details"
        open={pdfModalVisible}
        onOk={handlePdfGeneration}
        onCancel={handlePdfModalCancel}
        okText="PDF Genereren"
        cancelText="Annuleren"
        centered
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Bestandsnaam voor PDF:</Text>
            <Input
              placeholder="Bestandsnaam"
              value={bestandsnaam}
              onChange={(e) => setBestandsnaam(e.target.value)}
              onPressEnter={handlePdfGeneration}
              suffix=".pdf"
              style={{ marginTop: '8px' }}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              De bestandsnaam wordt automatisch aangevuld met .pdf
            </Text>
          </div>
          
          <div>
            <Text strong>Opmerking (optioneel):</Text>
            <TextArea
              placeholder="Voeg een opmerking toe aan de bestelling..."
              value={opmerking}
              onChange={(e) => setOpmerking(e.target.value)}
              rows={4}
              style={{ marginTop: '8px' }}
              maxLength={500}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              {opmerking.length}/500 karakters
            </Text>
          </div>

          {selectedBestelling && (
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
              <Text strong>Bestelling overzicht:</Text>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">Leverancier: </Text>
                <Text>{selectedBestelling.leverancier}</Text>
              </div>
              <div>
                <Text type="secondary">Aantal producten: </Text>
                <Text>{selectedBestelling.producten?.length || 0}</Text>
              </div>
              <div>
                <Text type="secondary">Datum: </Text>
                <Text>{dayjs(selectedBestelling.bestelDatum).format('DD/MM/YYYY')}</Text>
              </div>
            </div>
          )}
        </Space>
      </Modal>

      <FilterContainer 
        datum={datum}
        setDatum={setDatum}
        leverancier={leverancier}
        setLeverancier={setLeverancier}
        leveranciers={leveranciers}
      />
     
      <Space direction="vertical" size="large" style={{ width: '100%', marginTop: '16px', overflowY: "auto", maxHeight}}>
        {bestellen.map((item, index) => (
          <Bestelling
            key={index}
            bestelling={item}
            isExpanded={expandedCardIndex === index}
            onClick={() => toggleCard(index)}
            bestel={bestel}
            verwijderBestelling={verwijderBestelling}
          />
        ))}
        
        {bestellen.length === 0 && (
          <Card>
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
              Geen bestellingen om te plaatsen
            </Text>
          </Card>
        )}
      </Space>
    </div>
  );
};

const FilterContainer = ({ datum, setDatum, leverancier, setLeverancier, leveranciers }) => {

  const handleDateChange = (date, dateString) => {
    setDatum(date ? date.valueOf() : null);
  };

  const handleLeverancierChange = (value) => {
    setLeverancier(value);
  };

  const disabledDate = (current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  return (
      <Row gutter={24}>
        <Col span={12}>
          <DatePicker
            style={{ width: '100%' }}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            disabledDate={disabledDate}
            placeholder='Selecteer datum'
          />
        </Col>
        <Col span={12}>
          <Select
            style={{ width: '100%' }}
            placeholder="Selecteer leverancier"
            value={leverancier}
            onChange={handleLeverancierChange}
            showSearch
            optionFilterProp="children"
            allowClear
          >
            {leveranciers.map((lev, index) => (
              <Option key={index} value={lev.naam}>
                {lev.naam}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
  );
};

const Bestelling = ({
  bestelling,
  isExpanded,
  onClick,
  bestel,
  verwijderBestelling,
}) => {
  const getColumns = () => {
    const baseColumns = [
      {
        title: 'Product',
        dataIndex: 'product',
        key: 'product',
      },
      {
        title: 'Aantal',
        dataIndex: 'aantal',
        key: 'aantal',
        width: 100,
        render: (aantal, record) => `${aantal} ${record.eenheid || ''}`,
      }
    ];

    if (bestelling.isGesplitst) {
      return [
        {
          title: 'KookDatum',
          dataIndex: 'kookDatum',
          key: 'kookDatum',
          render: (text) => dayjs(text).format('DD/MM/YYYY'),
        },
        {
          title: 'Leerkracht',
          dataIndex: 'leerkracht',
          key: 'leerkracht',
        },
        ...baseColumns
      ];
    }
    
    return baseColumns;
  };

  return (
    <Card
      style={{ width: '100%' }}
      bodyStyle={{ padding: isExpanded ? '24px' : '12px 24px' }}
    >
      <Row justify="space-between" align="middle">
        <Col>
          <Space direction="vertical" size="small">
            <Space>
              <Text strong>Goedkeuringsdatum:</Text>
              <Text>
                {dayjs(bestelling.goedkeuringsDatum).format('DD/MM/YYYY')}
              </Text>
            </Space>
            <Space>
              <Text strong>Leverancier:</Text>
              <Text>{bestelling.leverancier}</Text>
            </Space>
            {bestelling.opmerking && (
              <Space>
                <Text strong>Opmerking:</Text>
                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                  {bestelling.opmerking}
                </Text>
              </Space>
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            <Button 
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                bestel(bestelling);
              }}
            >
              Bestel
            </Button>
            
            <Popconfirm
              title="Weet je zeker dat je deze bestelling wilt verwijderen?"
              onConfirm={(e) => {
                e?.stopPropagation();
                verwijderBestelling(bestelling);
              }}
              okText="Ja"
              cancelText="Nee"
            >
              <Button 
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Verwijder
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>

      {isExpanded && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Table
            dataSource={bestelling.producten}
            columns={getColumns()}
            rowKey={(record, index) => index}
            pagination={false}
            bordered
            size="middle"
          />
        </>
      )}
      
      <Divider style={{ margin: '16px 0 0 0' }} />
      <Row justify="center">
        <Button 
          type="text" 
          onClick={onClick}
          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
        >
          {isExpanded ? 'Minder info' : 'Meer info'}
        </Button>
      </Row>
    </Card>
  );
};

export default BestelScreen;